'use client';
import { Box, Card, Flex, Grid, GridItem, Icon, Input, ListCollection, Portal, Spinner, Stack } from '@chakra-ui/react';
import { ReactNode, useMemo, useState } from 'react';
import { InputGroup } from './input-group';
import { ChevronDown, Plus } from 'lucide-react';
import {
    ComboboxRoot,
    ComboboxControl,
    ComboboxInputElement,
    ComboboxTrigger,
    ComboboxContent,
    ComboboxItem,
    ComboboxPositioner,
    type ComboboxItem as Item,
    useComboboxBase,
} from './combobox-base';

interface ComboboxInputProps<T extends Item> {
    items: T[];
    filterItems?: (value: string) => void;
    onChange: (value: string | undefined) => void;
    renderItem: (item: T) => ReactNode;
    renderSelection?: (item: T) => ReactNode;
    onValueInput?: (value: string) => void;
    name: string;
    value: T | undefined;
    loading?: boolean;
    loadingMessage?: string;
    disabled?: boolean;
    readOnly?: boolean;
    autoFocus?: boolean;
    defaultOpen?: boolean;
    portalled?: boolean;
    onCreateNew?: () => void;
    /** True while a debounced search is pending but hasn't fired yet. Suppresses the "Create new" option during the debounce window. */
    pending?: boolean;
}

/**
 * ComboboxInput component for free-text input with optional selection from suggestions.
 *
 * Features:
 * - Allows entering custom text OR selecting from predefined options
 * - No clear button (user can manually clear by typing)
 * - Dropdown hidden when filtering yields no matches (no "No results" message)
 * - Input value persists on blur (doesn't revert if no selection made)
 * - onValueInput callback fired on every keystroke for real-time validation/processing
 *
 * For strict selection from options only, use ComboboxSelect instead.
 *
 * @example
 * ```tsx
 * <ComboboxInput
 *   items={emailSuggestions}
 *   value={selectedEmail}
 *   onChange={(value) => setEmail(value)} // Can be custom text or selected value
 *   onValueInput={(text) => validateEmail(text)}
 *   renderItem={(item) => <div>{item.label}</div>}
 *   name="email-input"
 * />
 * ```
 */
export const ComboboxInput = <T extends Item>({ portalled = true, ...props }: ComboboxInputProps<T>) => {
    const [openOptions, setOpenOptions] = useState(true);

    const {
        filterItems,
        items,
        onChange,
        name,
        value,
        renderItem,
        renderSelection = renderItem,
        onValueInput,
        loading,
        loadingMessage,
        disabled,
        autoFocus = false,
        defaultOpen = false,
        readOnly,
        onCreateNew,
        pending = false,
    } = props;

    const { inputRef, collection, hasSelectedValue, displayValue, moveCaretToEnd, handleValueChange, handleInput } = useComboboxBase({
        items,
        filterItems,
        value,
        onChange,
        onValueInput,
        name,
        creatable: true,
        clearable: false,
    });

    const showCreateNew = useMemo(() => {
        const displayRules = {
            hasCreateNew: !!onCreateNew,
            notLoading: !loading,
            notPending: !pending,
            hasInput: !!displayValue && displayValue.length > 0,
            noMatches: collection.items.length === 0,
        };

        return Object.values(displayRules).every(Boolean);
    }, [onCreateNew, loading, pending, displayValue, collection.items.length]);

    return (
        <ComboboxRoot
            flex={1}
            w="100%"
            name={name}
            collection={collection}
            value={hasSelectedValue && value ? [value.value] : []}
            disabled={disabled}
            readOnly={readOnly}
            inputValue={displayValue}
            autoFocus={autoFocus}
            defaultOpen={defaultOpen}
            openOnClick
            onValueChange={(e) => handleValueChange(e.value)}
            onInput={handleInput}
            onFocus={() => setOpenOptions(true)}
            onBlur={() => setOpenOptions(false)}>
            <ComboboxControl>
                <Grid>
                    <GridItem gridArea="1/1/2/3">
                        <InputGroup
                            w="100%"
                            endElement={
                                <Flex gap="8px" align="center">
                                    {loading && <Spinner size="sm" />}
                                    {!!items.length && (
                                        <ComboboxTrigger asChild>
                                            <Icon boxSize={4} color="#52525B">
                                                <ChevronDown />
                                            </Icon>
                                        </ComboboxTrigger>
                                    )}
                                </Flex>
                            }>
                            <ComboboxInputElement asChild aria-describedby={hasSelectedValue ? `${name}-selected-value` : undefined}>
                                <Input
                                    ref={inputRef}
                                    placeholder=""
                                    data-1p-ignore
                                    autoComplete="off"
                                    color={hasSelectedValue ? 'transparent' : undefined}
                                    caretColor={hasSelectedValue ? '#213547' : undefined}
                                    onFocus={moveCaretToEnd}
                                    onClick={moveCaretToEnd}
                                />
                            </ComboboxInputElement>
                        </InputGroup>
                    </GridItem>
                    {hasSelectedValue && (
                        <GridItem
                            fontSize="12px"
                            pl="12px"
                            h="36px"
                            gridArea="1/1/2/3"
                            w="calc(100% - 36px)"
                            pointerEvents="none"
                            id={`${name}-selected-value`}
                            role="status"
                            aria-live="polite">
                            <Flex gap="0" h="36px" alignItems="center" w="100%">
                                {value && renderSelection(value)}
                            </Flex>
                        </GridItem>
                    )}
                </Grid>
            </ComboboxControl>
            <Portal disabled={!portalled}>
                <ComboboxPositioner css={{ '--z-index': '2999 !important' }}>
                    {openOptions && (collection.items.length > 0 || loading || showCreateNew) && (
                        <ComboboxContent
                            boxShadow="none"
                            px="0"
                            py="0"
                            overflow="auto"
                            maxH="50vh"
                            fontSize="14px"
                            border="1px solid #E4E4E7"
                            overscrollBehavior="contain">
                            <Card.Root variant="elevated">
                                <Card.Body p={0}>
                                    {collection.items.length > 0 ? (
                                        (collection as unknown as ListCollection<T>).items.map((item) => (
                                            <ComboboxItem key={item.value} item={item} persistFocus height="auto" p="2">
                                                <Stack gap="0">{renderItem(item)}</Stack>
                                            </ComboboxItem>
                                        ))
                                    ) : loading ? (
                                        <Box p="2">{loadingMessage || 'Loading Items'}</Box>
                                    ) : showCreateNew ? (
                                        <Flex
                                            p="2"
                                            gap="1"
                                            alignItems="center"
                                            color="fg.muted"
                                            onPointerDown={(e) => {
                                                e.preventDefault();
                                                onCreateNew?.();
                                            }}
                                            _hover={{ cursor: 'pointer', textDecor: 'underline' }}>
                                            <Icon>
                                                <Plus />
                                            </Icon>
                                            Create "{displayValue}"
                                        </Flex>
                                    ) : undefined}
                                </Card.Body>
                            </Card.Root>
                        </ComboboxContent>
                    )}
                </ComboboxPositioner>
            </Portal>
        </ComboboxRoot>
    );
};
